import { BarChart3, Heart, Home, MoreHorizontal, Wind } from 'lucide-react';
import { useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useT } from '@/i18n/useT';

const TABS = [
  { to: '/', key: 'nav.home', Icon: Home, tour: 'tour-tab-home' },
  { to: '/salud', key: 'nav.health', Icon: Heart, tour: 'tour-tab-health' },
  { to: '/progreso', key: 'nav.progress', Icon: BarChart3, tour: 'tour-tab-progress' },
  { to: '/mas', key: 'nav.more', Icon: MoreHorizontal, tour: 'tour-tab-more' },
] as const;

/**
 * Bottom navigation with the craving button raised in the middle.
 * Long-pressing it opens emergency mode (docs/FEATURES.md#navegación).
 */
export function TabBar() {
  const navigate = useNavigate();
  const t = useT();
  // A ref, not a local: any re-render between pointerdown and pointerup would
  // otherwise lose the timer and swallow the tap.
  const pressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Set when the long press fires so the trailing click doesn't also open the
  // craving flow.
  const longPressFired = useRef(false);

  const startPress = () => {
    longPressFired.current = false;
    pressTimer.current = setTimeout(() => {
      pressTimer.current = undefined;
      longPressFired.current = true;
      navigate('/emergencia');
    }, 600);
  };

  const cancelPress = () => {
    clearTimeout(pressTimer.current);
    pressTimer.current = undefined;
  };

  // The short tap navigates on the real `click`, not on pointerup: on touch,
  // navigating during pointerup let the browser's trailing compatibility click
  // land on the just-mounted "Empezar" button (same spot), auto-starting the
  // wave. Handling the click here consumes that very event, so nothing leaks
  // through to the craving screen.
  const openCraving = () => {
    cancelPress();
    if (longPressFired.current) {
      longPressFired.current = false; // the long press already navigated away
      return;
    }
    navigate('/antojo');
  };

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/85 backdrop-blur-lg"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label={t('nav.ariaMain')}
    >
      <div className="mx-auto flex h-[var(--tabbar-height)] max-w-[var(--content-max)] items-center justify-around px-2">
        {TABS.slice(0, 2).map((tab) => (
          <Tab key={tab.to} to={tab.to} label={t(tab.key)} Icon={tab.Icon} tour={tab.tour} />
        ))}

        <button
          type="button"
          data-tour="tour-craving"
          onPointerDown={startPress}
          onPointerLeave={cancelPress}
          onPointerCancel={cancelPress}
          onClick={openCraving}
          onContextMenu={(e) => e.preventDefault()}
          className="-mt-7 flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-full bg-brand text-white shadow-lg transition-transform active:scale-95"
          aria-label={t('nav.craving.aria')}
        >
          <Wind size={26} aria-hidden />
          <span className="text-[10px] font-semibold">{t('nav.craving')}</span>
        </button>

        {TABS.slice(2).map((tab) => (
          <Tab key={tab.to} to={tab.to} label={t(tab.key)} Icon={tab.Icon} tour={tab.tour} />
        ))}
      </div>
    </nav>
  );
}

function Tab({ to, label, Icon, tour }: { to: string; label: string; Icon: typeof Home; tour?: string }) {
  return (
    <NavLink
      to={to}
      data-tour={tour}
      end={to === '/'}
      className={({ isActive }) =>
        `flex min-h-11 w-16 flex-col items-center justify-center gap-0.5 rounded-lg text-[11px] font-medium transition-colors ${
          isActive ? 'text-brand' : 'text-muted'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={22} strokeWidth={isActive ? 2.4 : 2} aria-hidden />
          <span className="text-center leading-tight">{label}</span>
        </>
      )}
    </NavLink>
  );
}
